import { useState, useEffect, useMemo } from 'react';
import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import { useIntl } from 'react-intl';
import {
  RequestQuoteAnalyticsEventParameters,
  requestQuoteAnalyticsEvents,
} from './analytics';
import { openCreateProjectModal } from './requestQuote.utils';
import { RequestQuoteEditableContent } from './RequestQuoteEditableContent';
import { RequestQuoteSubmitSection } from './RequestQuoteSubmitSection';
import { RequestQuoteSuccessContent } from './RequestQuoteSuccessContent';
import { useRequestQuoteTranslations } from './useRequestQuoteTranslations';
import {
  getCreateOrderPayload,
  createOrder,
  createContactRepMessage,
  RepSelectOptions,
  getShouldTriggerProjectConfirmationDialog,
  Sidebar,
  SidebarContent,
  SidebarTitle,
} from '../../../../../../UILibrary';
import { fetchProductJson } from '../../api/requests';
import { definedTranslation } from '../../i18n';

export const RequestQuote = () => {
  const intl = useIntl();
  const { UNIT_OF_MEASURE_VALUE_MAP } = useRequestQuoteTranslations();
  const { projectsLoading: isLoadingProjects } = useProjectsProvider();
  const { currentProject, setCurrentProjectId } = useCurrentProject();
  const { isProjectDataComplete } = useValidateProjectData();

  const [isOpen, setIsOpen] = useState(false);
  const [isCreateOrderLoading, setIsCreateOrderLoading] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState('');
  const [selectedVariantImg, setSelectedVariantImg] = useState<string | null>(
    null,
  );
  const [assignedRepId, setAssignedRepId] = useState('');
  const [purchasingQuantity, setPurchasingQuantity] = useState('');
  const [reps, setReps] = useState<RepSelectOptions[]>([]);
  const [hasSubmittedSuccessfully, setHasSubmittedSuccessfully] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    function onOpen() {
      const makeSeePricingMeaningfulMove = () => {
        const productSku = window.productAttributeJson?.sku?.value;

        if (!currentProject) {
          // Open the create project modal if no project is selected
          openCreateProjectModal();

          return;
        }

        if (productSku) {
          // Complete any missing project data
          if (!isProjectDataComplete(currentProject)) {
            projectsEditDialogConfig.openProjectEditDialog({
              projectId: currentProject?.externalId,
              successCallback: async (project: ProjectModel) => {
                const orderData = getCreateOrderPayload(
                  productSku,
                  project,
                  'See Pricing',
                );

                createOrder(orderData);
              },
            });

            return;
          }

          const orderData = getCreateOrderPayload(
            productSku,
            currentProject,
            'See Pricing',
          );

          createOrder(orderData);
        }
      };

      makeSeePricingMeaningfulMove();

      // If there is only one configurable variant, we can auto-select it
      if (
        window.productConfigurableJson &&
        Object.keys(window.productConfigurableJson.images)?.length === 1
      ) {
        const firstVariantId = Object.keys(
          window.productConfigurableJson.images,
        )[0];
        setSelectedVariant(firstVariantId);
        setSelectedVariantImg(
          window.productConfigurableJson?.images?.[firstVariantId]?.[0]?.url,
        );
      }

      // If there is an activeChild query param (a variant), initialize the selected variant to it
      const urlParams = new URLSearchParams(window.location.search);
      const activeChild = urlParams.get('activeChild');
      if (
        activeChild &&
        window.productConfigurableJson?.images?.[activeChild]?.[0]?.url
      ) {
        setSelectedVariant(activeChild);
        setSelectedVariantImg(
          window.productConfigurableJson?.images?.[activeChild]?.[0]?.url,
        );
      }
    }

    const openRequestQuoteSidebar = () => {
      document.body.style.overflow = 'hidden';
      setIsOpen(true);
      requestQuoteAnalyticsEvents.requestQuoteCtaClicked(); // Drawer being opened is proxy for for request quote CTA click

      onOpen();
    };

    const handleOpen = () => {
      if (!currentProject) {
        // Open the create project modal if no project is selected
        openCreateProjectModal();

        return;
      }

      const shouldTriggerProjectConfirmationDialog =
        getShouldTriggerProjectConfirmationDialog();

      if (shouldTriggerProjectConfirmationDialog) {
        window.dispatchEvent(
          new CustomEvent('open-project-selection-dialog', {
            detail: {
              currentProjectId: currentProject?.externalId,
              analyticsEventLocation: 'Request quote',
              onCurrentProjectSave: () => {
                openRequestQuoteSidebar();
              },
            },
          }),
        );

        return;
      }

      // Complete any missing project data
      if (!isProjectDataComplete(currentProject)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: currentProject?.externalId,
          successCallback: openRequestQuoteSidebar,
        });

        return;
      }

      openRequestQuoteSidebar();
    };

    window.addEventListener('open-request-quote-sidebar', handleOpen);

    return () => {
      window.removeEventListener('open-request-quote-sidebar', handleOpen);
    };
  }, [currentProject, isProjectDataComplete]);

  useEffect(() => {
    const handleProjectUpdated = (
      event: CustomEvent<{
        projectId: string;
        projectName: string;
        projectType: string;
        projectLocation: string;
      }>,
    ) => {
      const { projectId } = event.detail;

      if (projectId) {
        setCurrentProjectId(projectId);
      }
    };

    window.addEventListener('project-updated', handleProjectUpdated);

    return () => {
      window.removeEventListener('project-updated', handleProjectUpdated);
    };
  }, [setCurrentProjectId]);

  const resetState = () => {
    setAssignedRepId('');
    setPurchasingQuantity('');
    setSelectedVariant('');
    setSelectedVariantImg(null);
    setHasSubmittedSuccessfully(false);
    setErrorMessage('');
  };

  const handleClose = (dispatchDrawerClosedEvent = true) => {
    resetState();
    document.body.style.overflow = 'auto';
    setIsOpen(false);

    if (dispatchDrawerClosedEvent) {
      requestQuoteAnalyticsEvents.drawerClosed(getAnalyticsEventOptions());
    }
  };

  const onLearnMoreClicked = () => {
    handleClose(false);
    requestQuoteAnalyticsEvents.learnMoreCtaClicked(getAnalyticsEventOptions());
  };

  const makeRequestQuoteMeaningfulMove = async () => {
    setIsCreateOrderLoading(true);

    try {
      const productSku = window.productAttributeJson?.sku?.value;

      // Fetch variant data if not available
      const shouldFetchVariantData =
        selectedVariant &&
        !window.productConfigurableJson?.child_list?.[selectedVariant];

      if (shouldFetchVariantData) {
        const response = await fetchProductJson(selectedVariant);
        if (!response.success) {
          setHasSubmittedSuccessfully(false);
          setIsCreateOrderLoading(false);
          setErrorMessage('Failed to fetch variant data.');

          return;
        }

        if (
          window.productConfigurableJson &&
          window.productConfigurableJson['child_list']
        ) {
          window.productConfigurableJson['child_list'][selectedVariant] =
            response.data;
        }
      }

      const selectedVariantManufacturerSku =
        window.productConfigurableJson?.child_list?.[selectedVariant]
          ?.manufacturer_sku?.value ??
        // TODO: are we ok to use a fallback here?
        window.productAttributeJson?.manufacturer_sku?.value;

      const quoteDetails = `SKU: ${selectedVariantManufacturerSku}; Intended purchasing quantity: ${purchasingQuantity} ${pluralUnitOfMeasure}`;

      if (!productSku || !selectedVariantManufacturerSku) {
        setHasSubmittedSuccessfully(false);
        setIsCreateOrderLoading(false);
        setErrorMessage('Product SKU or variant manufacturer SKU is missing.');

        return;
      }

      if (!currentProject) {
        openCreateProjectModal();

        return;
      }

      const orderHandler = async (project: ProjectModel) => {
        const orderData = getCreateOrderPayload(
          productSku,
          project,
          'Request quote',
          { quoteDetails },
        );
        await createOrder(orderData);

        requestQuoteAnalyticsEvents.requestQuoteCompleted({
          ...getAnalyticsEventOptions(),
        });

        setHasSubmittedSuccessfully(true);
      };

      // Complete any missing project data
      if (!isProjectDataComplete(currentProject)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: currentProject?.externalId,
          successCallback: async (project: ProjectModel) => {
            await orderHandler(project);

            setIsCreateOrderLoading(false);
          },
        });
        return;
      }

      await orderHandler(currentProject);
    } catch {
      setHasSubmittedSuccessfully(false);
      setErrorMessage('Failed to create order.');
    } finally {
      setIsCreateOrderLoading(false);
    }
  };

  const onSubmitRequestClicked = () => {
    makeRequestQuoteMeaningfulMove();
    const productId =
      selectedVariant !== ''
        ? selectedVariant
        : window.productAttributeJson?.product_id?.value;
    const hasColorway = !!window?.productConfigurableJson?.attributes?.[478];
    const colorway =
      window?.productConfigurableJson?.images?.[selectedVariant]?.[0]
        ?.color_name;

    if (productId && assignedRepId) {
      createContactRepMessage({
        product_id: parseInt(productId),
        project_id: parseInt(currentProject?.externalId?.toString() ?? ''),
        manufacturer: window.productAttributeJson?.manufacturer?.value ?? '',
        rep_id: assignedRepId,
        product_name: window.productAttributeJson?.name?.value ?? '',
        message_source: 'Request quote',
        message_type: 'quote',
        quote: {
          ...(hasColorway && colorway ? { colorway: colorway } : {}),
          purchasing_qty: purchasingQuantity,
          unit_of_measure: pluralUnitOfMeasure,
        },
      });
    }
  };

  function inboxClickHandler() {
    requestQuoteAnalyticsEvents.goToInboxClicked(getAnalyticsEventOptions());
    handleClose(false);
  }

  const getAnalyticsEventOptions = (): RequestQuoteAnalyticsEventParameters => {
    return {
      projectId: currentProject?.externalId?.toString() || undefined,
      projectName: currentProject?.title,
      quantity: purchasingQuantity || undefined,
      unitType: pluralUnitOfMeasure || undefined,
    };
  };

  const pluralUnitOfMeasure =
    UNIT_OF_MEASURE_VALUE_MAP[
      window?.productAttributeJson?.pricing_unit_of_measure?.value ?? ''
    ];

  const isSubmitDisabled =
    purchasingQuantity === '' ||
    parseInt(purchasingQuantity) === 0 ||
    isLoadingProjects ||
    assignedRepId === '' ||
    assignedRepId === undefined ||
    isCreateOrderLoading;

  const errorTooltipContent = useMemo(() => {
    if (purchasingQuantity === '' || parseInt(purchasingQuantity) === 0) {
      return (
        <p>
          {intl?.formatMessage(
            definedTranslation.requestQuote.nonZeroPurchaseQuantityError,
          )}
        </p>
      );
    } else if (assignedRepId === '' || assignedRepId === undefined) {
      return (
        <p>
          {intl?.formatMessage(
            definedTranslation.requestQuote.assignRepresentativeError,
          )}
        </p>
      );
    } else if (reps?.length === 0) {
      return (
        <p>
          {intl?.formatMessage(definedTranslation.requestQuote.noRepsError)}
        </p>
      );
    }
    return <></>;
  }, [purchasingQuantity, assignedRepId, reps?.length, intl]);

  return (
    <Sidebar isOpen={isOpen} handleClose={handleClose}>
      <SidebarTitle>
        {intl?.formatMessage(
          definedTranslation.requestQuote.pricingAndQuoteLabel,
        )}
      </SidebarTitle>
      <SidebarContent>
        {hasSubmittedSuccessfully ? (
          <RequestQuoteSuccessContent
            selectedVariantImg={selectedVariantImg}
            onInboxClick={inboxClickHandler}
          />
        ) : (
          <RequestQuoteEditableContent
            purchasingQuantity={purchasingQuantity}
            selectedVariant={selectedVariant}
            selectedVariantImg={selectedVariantImg}
            unitOfMeasureMap={UNIT_OF_MEASURE_VALUE_MAP}
            setPurchasingQuantity={setPurchasingQuantity}
            setSelectedVariant={setSelectedVariant}
            setSelectedVariantImg={setSelectedVariantImg}
            setReps={setReps}
            setAssignedRepId={setAssignedRepId}
            getAnalyticsEventOptions={getAnalyticsEventOptions}
          />
        )}
      </SidebarContent>
      {!hasSubmittedSuccessfully ? (
        <RequestQuoteSubmitSection
          isLearnMoreButtonDisabled={isCreateOrderLoading}
          isSubmitButtonDisabled={isSubmitDisabled}
          isCreateOrderLoading={isCreateOrderLoading}
          errorTooltipContent={errorTooltipContent}
          errorMessage={errorMessage}
          onLearnMoreClick={onLearnMoreClicked}
          onSubmit={onSubmitRequestClicked}
        />
      ) : null}
    </Sidebar>
  );
};
